import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  useSelectedRecommendedProjectSelector,
  useCurrentGeoRasterDataSelector,
  useCurrentDataLayerTypeSelector,
} from "../../../../store/projects/selectors";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type ChartData = {
  labels: any[];
  values: any[];
};

const ReDemAccordionBody = () => {
  const selectedReProject = useSelectedRecommendedProjectSelector();
  const geoRasterData = useCurrentGeoRasterDataSelector();
  const currentLayer = useCurrentDataLayerTypeSelector();

  useEffect(() => {
    if (currentLayer === "dem" && geoRasterData) {
      processChartData(geoRasterData?.data);
    }
  }, [selectedReProject, geoRasterData]);

  const processChartData = async (geoRasterData: any) => {
    let pixelArea: number;

    if (geoRasterData.pixelHeight && geoRasterData.pixelWidth) {
      pixelArea = 90 * 90; // Calculate the area of a pixel in square meters
      const values = geoRasterData.values[0];
      const demCounts: any = {};
      const pixelAreaHectares = pixelArea / 10000; // Convert square meters to hectares

      // Loop through each pixel
      for (let y = 0; y < geoRasterData.height; y++) {
        for (let x = 0; x < geoRasterData.width; x++) {
          const pixelValue = values[y][x];

          if (pixelValue > 0) {
            // Use pixel value directly as the key
            if (!demCounts[pixelValue]) {
              demCounts[pixelValue] = 0;
            }

            // Increment the count for the corresponding pixel value
            demCounts[pixelValue]++;
          }
        }
      }

      // Calculate the total area for each canopy height value
      const demCountsTotal: any = {};
      for (const height in demCounts) {
        const count = demCounts[height];
        demCountsTotal[height] = count * pixelAreaHectares; // Total area in hectares
      }

      // Extract chart data
      const labels = Object.keys(demCountsTotal).map(Number); // Ensure labels are numbers
      const chartValues = Object.values(demCountsTotal);

      // Sort labels and corresponding values
      const sortedData = labels
        .map((label, index) => ({
          label,
          value: chartValues[index],
        }))
        .sort((a, b) => a.label - b.label);

      // Set sorted data
      setChartData({
        labels: sortedData.map((item) => item.label),
        values: sortedData.map((item) => item.value),
      });
    } else {
      console.error("Insufficient data to calculate the area");
    }
  };

  const [chartData, setChartData] = useState<ChartData>({
    labels: [],
    values: [],
  });

  const options = {
    plugins: {
      title: {
        display: true,
        text: "DEM",
      },
      legend: {
        display: false,
        position: "bottom" as const,
      },
    },
    responsive: true,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Elevation in meters",
        },
        grid: {
          display: false,
        },
      },
      y: {
        title: {
          display: true,
          text: "Area(Hectares)",
        },
        grid: {
          display: true,
        },
      },
    },
  };

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        // label: "lulc",
        display: false,
        data: chartData?.values,
        backgroundColor: "#482F8B",
        barThickness: 30,
        barPercentage: 0.5,
      },
    ],
  };

  return (
    <div className="font-primary">
      <div>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default ReDemAccordionBody;
